import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import { Link } from "expo-router";
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Gift,
  HeartHandshake,
  MapPin,
  MessageCircle,
  Phone,
  RotateCw,
  ShieldCheck,
  Sparkles,
  Users,
} from "../../../components/lucide-icons";
import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Image,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import { Badge, Button, Panel } from "../../../components/ui";
import { AppShell } from "../../../components/layout";
import { useReducedMotion } from "../../../hooks/useReducedMotion";
import { supportsNativeDriver } from "../../../lib/animation";
import { layout, motion, radii, semantic, shadows, spacing } from "../../../theme/tokens";
import { ArrangementCase, ArrangementProduct, MemorialHubPreview, MissingItem } from "../types";
import { monarchDemoSite } from "../data/monarch-demo-site";
import { formatDecedentName, formatServiceDate } from "../utils/format-arrangement";
import { arrangementRoutes } from "../routes";

type IconComponent = (props: { color: string; size: number; strokeWidth?: number }) => ReactNode;

const monarchBlueLogo = require("../../../../assets/monarch-blue-logo.png");

const stageLabels = ["Path", "Details", "Service", "Products", "Review"];

export function MonarchDemoShell({
  children,
  activeLabel,
  footer,
  role = "family",
  accessibilityLabel = "Secure arrangement workspace",
  brandTitle = "Secure arrangement workspace",
  homeHref = arrangementRoutes.start,
  showContactStrip = true,
}: PropsWithChildren<{
  activeLabel?: string;
  footer?: ReactNode;
  role?: "family" | "staff" | "public";
  accessibilityLabel?: string;
  brandTitle?: string;
  homeHref?: string;
  showContactStrip?: boolean;
}>) {
  return (
    <AppShell
      role={role}
      navigation="none"
      accessibilityLabel={accessibilityLabel}
      requireStaffSession={role === "staff"}
    >
      <LinearGradient
        colors={[semantic.surface.raised, semantic.surface.canvas, semantic.surface.canvasAlt]}
        style={styles.root}
      >
        <ScrollView contentInsetAdjustmentBehavior="automatic" contentContainerStyle={styles.scroll}>
          <View style={styles.container}>
            <View style={styles.header}>
              <Link href={homeHref as never} asChild>
                <Pressable style={styles.brandLockup}>
                  <MonarchMark />
                  <View style={styles.brandTextBlock}>
                    <Text style={styles.brandTitle}>{brandTitle}</Text>
                    <Text style={styles.brandSubline}>{monarchDemoSite.cityLine}</Text>
                  </View>
                </Pressable>
              </Link>
              {activeLabel ? <Badge label={activeLabel} tone="forest" /> : null}
            </View>
            {children}
            {footer ?? (showContactStrip ? <ContactStrip /> : null)}
          </View>
        </ScrollView>
      </LinearGradient>
    </AppShell>
  );
}

export function MonarchMark() {
  return (
    <View style={styles.mark}>
      <Image source={monarchBlueLogo} resizeMode="contain" style={styles.markLogo} />
    </View>
  );
}

export function ContactStrip() {
  return (
    <Panel style={styles.contactStrip}>
      <View style={styles.contactItem}>
        <Phone color={semantic.action.primary} size={18} strokeWidth={1.8} />
        <Text style={styles.contactText}>{monarchDemoSite.phone}</Text>
      </View>
      <View style={styles.contactItem}>
        <MapPin color={semantic.text.muted} size={18} strokeWidth={1.8} />
        <Text style={styles.contactText}>{monarchDemoSite.address}</Text>
      </View>
      <Text style={styles.poweredBy}>Secure arrangement workspace</Text>
    </Panel>
  );
}

export function DemoSection({
  label,
  title,
  body,
  children,
  style,
}: PropsWithChildren<{
  label?: string;
  title: string;
  body?: string;
  style?: StyleProp<ViewStyle>;
}>) {
  return (
    <View style={[styles.section, style]}>
      <View style={styles.sectionCopy}>
        {label ? <Text style={styles.sectionLabel}>{label}</Text> : null}
        <Text style={styles.sectionTitle}>{title}</Text>
        {body ? <Text style={styles.sectionBody}>{body}</Text> : null}
      </View>
      {children}
    </View>
  );
}

export function ArrangementProgress({ currentIndex }: { currentIndex: number }) {
  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={`Arrangement progress: ${stageLabels[currentIndex] ?? "Review"}, step ${currentIndex + 1} of ${stageLabels.length}`}
      accessibilityValue={{ min: 1, max: stageLabels.length, now: currentIndex + 1 }}
      style={styles.progressRail}
    >
      {stageLabels.map((stage, index) => {
        const active = index <= currentIndex;
        return (
          <View key={stage} style={styles.progressItem}>
            <View style={[styles.progressDot, active ? styles.progressDotActive : null]}>
              {active ? <Check color={semantic.text.inverse} size={12} strokeWidth={2.2} /> : null}
            </View>
            <Text style={[styles.progressLabel, active ? styles.progressLabelActive : null]}>{stage}</Text>
          </View>
        );
      })}
    </View>
  );
}

export function ArrangementChoiceCard({
  title,
  subtitle,
  active,
  icon: Icon,
  onPress,
}: {
  title: string;
  subtitle: string;
  active?: boolean;
  icon: IconComponent;
  onPress?: () => void;
}) {
  const { reduceMotion } = useReducedMotion();
  const [focused, setFocused] = useState(false);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${subtitle}`}
      accessibilityHint={active ? "Selected" : "Select this arrangement path"}
      accessibilityState={{ selected: active }}
      onBlur={() => setFocused(false)}
      onFocus={() => setFocused(true)}
      onPress={() => {
        if (!reduceMotion) Haptics.selectionAsync().catch(() => undefined);
        onPress?.();
      }}
      style={({ pressed }) => [
        styles.choiceCard,
        active ? styles.choiceCardActive : null,
        pressed ? styles.choiceCardPressed : null,
        focused ? styles.controlFocused : null,
      ]}
    >
      <View style={[styles.choiceIcon, active ? styles.choiceIconActive : null]}>
        <Icon color={active ? semantic.text.inverse : semantic.action.primary} size={20} strokeWidth={1.9} />
      </View>
      <View style={styles.choiceCopy}>
        <Text style={styles.choiceTitle}>{title}</Text>
        <Text style={styles.choiceSubtitle}>{subtitle}</Text>
      </View>
      <ChevronRight color={active ? semantic.action.primary : semantic.text.secondary} size={18} strokeWidth={1.8} />
    </Pressable>
  );
}

export function AiGuidePanel({ children }: PropsWithChildren) {
  return (
    <LinearGradient
      colors={semantic.gradient.forest}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.aiPanel}
    >
      <View style={styles.aiIcon}>
        <Sparkles color={semantic.accent.soft} size={18} strokeWidth={1.9} />
      </View>
      <View style={styles.aiCopy}>
        <Text style={styles.aiTitle}>Next best step</Text>
        <Text style={styles.aiBody}>{children}</Text>
      </View>
    </LinearGradient>
  );
}

export function InteractiveProductViewer({
  product,
  selected,
  onSelect,
}: {
  product: ArrangementProduct;
  selected?: boolean;
  onSelect: (productId: string) => void;
}) {
  const { reduceMotion } = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const rotate = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reduceMotion) rotate.setValue(0);
    return () => rotate.stopAnimation();
  }, [reduceMotion, rotate]);

  const rotation = rotate.interpolate({
    inputRange: [-1, 1],
    outputRange: ["-3deg", "3deg"],
  });

  const toneColors = productToneColors(product.imageTone);

  const resetRotation = () => {
    if (reduceMotion) {
      rotate.setValue(0);
      return;
    }
    rotate.stopAnimation();
    Animated.timing(rotate, {
      toValue: 0,
      duration: motion.feedbackDuration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: supportsNativeDriver,
    }).start();
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${product.name}. ${product.description}. ${product.priceLabel}`}
      accessibilityHint={selected ? "Selected" : "Select this product"}
      accessibilityState={{ selected }}
      onBlur={() => setFocused(false)}
      onFocus={() => setFocused(true)}
      onPress={() => {
        if (!reduceMotion) Haptics.selectionAsync().catch(() => undefined);
        onSelect(product.id);
      }}
      onPressIn={() => {
        if (reduceMotion) return;
        rotate.stopAnimation();
        Animated.timing(rotate, {
          toValue: selected ? -1 : 1,
          duration: motion.feedbackDuration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: supportsNativeDriver,
        }).start();
      }}
      onPressOut={resetRotation}
      style={({ pressed }) => [
        styles.productCard,
        selected ? styles.productCardSelected : null,
        pressed ? styles.productCardPressed : null,
        focused ? styles.controlFocused : null,
      ]}
    >
      <Animated.View style={reduceMotion ? null : { transform: [{ rotate: rotation }] }}>
        <LinearGradient colors={toneColors} style={styles.productVisual}>
          <View style={styles.productShine} />
          <View style={styles.productBase} />
          <RotateCw color={semantic.overlay.dark} size={18} strokeWidth={1.6} />
        </LinearGradient>
      </Animated.View>
      <View style={styles.productCopy}>
        <View style={styles.productHeader}>
          <Text style={styles.productName}>{product.name}</Text>
          {selected ? <Badge label="Selected" tone="forest" /> : null}
        </View>
        <Text style={styles.productDescription}>{product.description}</Text>
        <Text style={styles.productPrice}>{product.priceLabel}</Text>
        <View style={styles.tagRow}>
          {product.tags.map((tag) => (
            <View key={tag} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
            </View>
          ))}
        </View>
      </View>
    </Pressable>
  );
}

export function ProductCarousel({
  products,
  selectedId,
  onSelect,
}: {
  products: ArrangementProduct[];
  selectedId?: string;
  onSelect: (productId: string) => void;
}) {
  const { reduceMotion } = useReducedMotion();
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.productRail}
    >
      {products.map((product) => (
        <InteractiveProductViewer
          key={product.id}
          product={product}
          selected={selectedId === product.id}
          onSelect={onSelect}
        />
      ))}
    </ScrollView>
  );
}

export function DigitalServiceFolderPreview({ arrangementCase }: { arrangementCase: ArrangementCase }) {
  return (
    <Panel style={styles.folderPanel}>
      <View style={styles.folderHeader}>
        <View style={styles.folderIcon}>
          <FileText color={semantic.accent.foreground} size={22} strokeWidth={1.8} />
        </View>
        <View style={styles.folderCopy}>
          <Text style={styles.folderTitle}>Digital service folder draft ready</Text>
          <Text style={styles.folderBody}>
            Guests can open a QR-accessible program now. Monarch can still print later after
            the portrait and pallbearers are confirmed.
          </Text>
        </View>
      </View>
      <View style={styles.folderPreviewRow}>
        <View style={styles.qrBox}>
          <QRCode value={`https://wehonor.app/monarch/folder/${arrangementCase.id}`} size={96} />
        </View>
        <View style={styles.orderList}>
          {arrangementCase.digitalFolder.orderOfService.slice(0, 5).map((item, index) => (
            <View key={item} style={styles.orderItem}>
              <Text style={styles.orderNumber}>{index + 1}</Text>
              <Text style={styles.orderText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>
    </Panel>
  );
}

export function GuestbookSettingsCard({ arrangementCase }: { arrangementCase: ArrangementCase }) {
  const settings = [
    "Allow guestbook messages",
    "Allow photo uploads",
    "Allow private messages to family",
    "Capture email and phone consent",
  ];

  return (
    <Panel style={styles.compactPanel}>
      <View style={styles.inlineHeader}>
        <MessageCircle color={semantic.action.primary} size={20} strokeWidth={1.8} />
        <Text style={styles.panelSmallTitle}>Monarch-owned guestbook</Text>
      </View>
      <View style={styles.stack}>
        {settings.map((setting) => (
          <View key={setting} style={styles.checkRow}>
            <Check color={semantic.status.success.foreground} size={16} strokeWidth={2} />
            <Text style={styles.checkText}>{setting}</Text>
          </View>
        ))}
      </View>
      <Text style={styles.noteText}>
        Each approved submission becomes a relationship-tagged CRM record attached to
        {` ${formatDecedentName(arrangementCase.decedent)}'s`} memorial.
      </Text>
    </Panel>
  );
}

export function KeepsakeSelectionGrid({
  products,
  selectedIds,
  onToggle,
}: {
  products: ArrangementProduct[];
  selectedIds: string[];
  onToggle: (productId: string) => void;
}) {
  const { reduceMotion } = useReducedMotion();
  return (
    <View style={styles.keepsakeGrid}>
      {products.map((product) => {
        const selected = selectedIds.includes(product.id);
        return (
          <Pressable
            key={product.id}
            accessibilityRole="checkbox"
            accessibilityLabel={`${product.name}. ${product.priceLabel}`}
            accessibilityState={{ checked: selected }}
            onPress={() => {
              if (!reduceMotion) Haptics.selectionAsync().catch(() => undefined);
              onToggle(product.id);
            }}
            style={[styles.keepsakeCard, selected ? styles.keepsakeCardSelected : null]}
          >
            <View style={styles.keepsakeIcon}>
              <Gift color={selected ? semantic.text.inverse : semantic.accent.foreground} size={20} strokeWidth={1.8} />
            </View>
            <Text style={styles.keepsakeTitle}>{product.name}</Text>
            <Text style={styles.keepsakeBody}>{product.priceLabel}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export function DirectorCaseCard({
  arrangementCase,
  href,
}: {
  arrangementCase: ArrangementCase;
  href: string;
}) {
  return (
    <Link href={href as never} asChild>
      <Pressable style={({ pressed }) => [styles.caseCard, pressed ? styles.choiceCardPressed : null]}>
        <View style={styles.caseTopline}>
          <Badge label="Immediate Need" tone="cedar" />
          <Text style={styles.caseTime}>Submitted 12 minutes ago</Text>
        </View>
        <Text style={styles.caseTitle}>{formatDecedentName(arrangementCase.decedent)} Arrangement Request</Text>
        <Text style={styles.caseMeta}>
          Family contact: {arrangementCase.planner.firstName} {arrangementCase.planner.lastName} · {arrangementCase.planner.phone}
        </Text>
        <View style={styles.caseFacts}>
          <Fact icon={ClipboardCheck} label="Disposition" value="Burial" />
          <Fact icon={FileText} label="Statement" value="Draft ready · 4 fields need review" />
          <Fact icon={CalendarDays} label="Events" value="Visitation, Mass, Graveside, Reception" />
        </View>
        <View style={styles.caseActions}>
          <Button label="Open Case" compact />
          <Button label="Open Statement" variant="ghost" compact />
        </View>
      </Pressable>
    </Link>
  );
}

export function DirectorSummaryPanel({ arrangementCase }: { arrangementCase: ArrangementCase }) {
  return (
    <Panel spotlight style={styles.directorSummary}>
      <View style={styles.inlineHeader}>
        <Sparkles color={semantic.accent.foreground} size={22} strokeWidth={1.8} />
        <Text style={styles.panelSmallTitle}>Director Summary</Text>
      </View>
      <Text style={styles.directorSummaryText}>{arrangementCase.aiSummary}</Text>
    </Panel>
  );
}

export function MissingItemsChecklist({ items }: { items: MissingItem[] }) {
  return (
    <Panel style={styles.compactPanel}>
      <View style={styles.inlineHeader}>
        <ShieldCheck color={semantic.action.primary} size={20} strokeWidth={1.8} />
        <Text style={styles.panelSmallTitle}>Missing information</Text>
      </View>
      <View style={styles.stack}>
        {items.map((item) => (
          <View key={item.id} style={styles.missingItem}>
            <View style={[styles.severityDot, item.severity === "required" ? styles.requiredDot : null]} />
            <View style={styles.missingCopy}>
              <Text style={styles.missingLabel}>{item.label}</Text>
              <Text style={styles.missingSection}>{item.section} · {item.severity}</Text>
            </View>
          </View>
        ))}
      </View>
    </Panel>
  );
}

export function MemorialHubPreviewPanel({ preview }: { preview: MemorialHubPreview }) {
  return (
    <Panel spotlight style={styles.memorialPanel}>
      <View style={styles.memorialHero}>
        <View style={styles.memorialPortrait}>
          <HeartHandshake color={semantic.accent.foreground} size={32} strokeWidth={1.5} />
        </View>
        <View style={styles.memorialCopy}>
          <Text style={styles.memorialName}>{preview.decedentName}</Text>
          <Text style={styles.memorialBody}>{preview.obituaryDraft}</Text>
        </View>
      </View>
      <View style={styles.memorialColumns}>
        <View style={styles.memorialColumn}>
          <Text style={styles.panelSmallTitle}>Service timeline</Text>
          {preview.serviceTimeline.map((event) => (
            <View key={event.id} style={styles.timelineItem}>
              <Text style={styles.timelineTitle}>{event.title}</Text>
              <Text style={styles.timelineMeta}>{formatServiceDate(event.date, event.startTime)}</Text>
              <Text style={styles.timelineMeta}>{event.locationName ?? "Location to be confirmed"}</Text>
            </View>
          ))}
        </View>
        <View style={styles.memorialColumn}>
          <Text style={styles.panelSmallTitle}>Guestbook + CRM</Text>
          {preview.crmSignals.map((signal) => (
            <View key={signal} style={styles.checkRow}>
              <Users color={semantic.status.success.foreground} size={16} strokeWidth={1.8} />
              <Text style={styles.checkText}>{signal}</Text>
            </View>
          ))}
          <View style={styles.qrBoxSmall}>
            <QRCode value={preview.qrValue} size={86} />
          </View>
        </View>
      </View>
    </Panel>
  );
}

export function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: IconComponent;
  label: string;
  value: string;
}) {
  return (
    <View style={styles.fact}>
      <Icon color={semantic.text.muted} size={17} strokeWidth={1.8} />
      <View style={styles.factCopy}>
        <Text style={styles.factLabel}>{label}</Text>
        <Text style={styles.factValue}>{value}</Text>
      </View>
    </View>
  );
}

export function NextActionBar({ children }: PropsWithChildren) {
  return <View style={styles.nextActionBar}>{children}</View>;
}

function productToneColors(tone: ArrangementProduct["imageTone"]): [string, string, string] {
  // Material tones are illustration color data for the product preview, not UI tokens.
  const toneMap: Record<ArrangementProduct["imageTone"], [string, string, string]> = {
    oak: ["#b17b43", "#d5ad77", "#7a4d27"],
    silver: ["#d8dad8", "#f7f8f6", "#9ba0a0"],
    white: ["#faf8ef", "#ffffff", "#d4c8ae"],
    pine: ["#c69b62", "#e2c08a", "#9a6e3c"],
    marble: ["#e9e7df", "#ffffff", "#b8b6ac"],
    bronze: ["#a26c3d", "#d39a64", "#6b442b"],
    gold: ["#d6b56c", "#f3dfa8", "#8f6b2f"],
    ivory: ["#efe7d5", "#fffaf0", "#cdbb95"],
    green: ["#567962", "#9eb69a", "#2e4d3c"],
  };

  return toneMap[tone];
}

const styles = StyleSheet.create({
  controlFocused: {
    borderColor: semantic.focus.ring,
    borderWidth: 2,
  },
  safeArea: {
    flex: 1,
    backgroundColor: semantic.surface.raised,
  },
  root: {
    flex: 1,
  },
  scroll: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
  },
  container: {
    width: "100%",
    maxWidth: layout.maxWidth,
    alignSelf: "center",
    gap: spacing.xl,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  brandLockup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flexShrink: 1,
  },
  mark: {
    width: 150,
    height: 76,
    borderRadius: 8,
    borderCurve: "continuous",
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    padding: 6,
  },
  markLogo: {
    width: 136,
    height: 70,
  },
  markText: {
    color: semantic.accent.soft,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 26,
    lineHeight: 28,
  },
  brandTextBlock: {
    gap: 2,
    minWidth: 0,
    flexShrink: 1,
  },
  brandTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 25,
    lineHeight: 28,
    flexShrink: 1,
  },
  brandSubline: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
    flexShrink: 1,
  },
  contactStrip: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  contactText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 18,
    flexShrink: 1,
  },
  poweredBy: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  section: {
    gap: spacing.lg,
  },
  sectionCopy: {
    gap: 8,
    maxWidth: 760,
  },
  sectionLabel: {
    color: semantic.text.muted,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 42,
    lineHeight: 44,
  },
  sectionBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 24,
  },
  progressRail: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    padding: 10,
    borderRadius: radii.lg,
    borderCurve: "continuous",
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  progressItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    minWidth: 96,
  },
  progressDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: semantic.overlay.ink,
    alignItems: "center",
    justifyContent: "center",
  },
  progressDotActive: {
    backgroundColor: semantic.action.primary,
  },
  progressLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
  },
  progressLabelActive: {
    color: semantic.text.primary,
  },
  choiceCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderRadius: radii.md,
    borderCurve: "continuous",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    backgroundColor: semantic.overlay.inverseStrong,
    padding: 16,
    minHeight: 106,
    flexBasis: 260,
    flexGrow: 1,
    ...shadows.soft,
  },
  choiceCardActive: {
    borderColor: semantic.action.primary,
    backgroundColor: semantic.surface.selected,
  },
  choiceCardPressed: {
    opacity: 0.88,
  },
  choiceIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  choiceIconActive: {
    backgroundColor: semantic.action.primary,
  },
  choiceCopy: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  choiceTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
    lineHeight: 20,
  },
  choiceSubtitle: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
  },
  aiPanel: {
    borderRadius: radii.lg,
    borderCurve: "continuous",
    padding: 18,
    flexDirection: "row",
    gap: 14,
    alignItems: "flex-start",
  },
  aiIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: semantic.overlay.goldSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  aiCopy: {
    flex: 1,
    gap: 5,
  },
  aiTitle: {
    color: semantic.accent.soft,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
  },
  aiBody: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 21,
  },
  productRail: {
    gap: 14,
    paddingRight: 24,
  },
  productCard: {
    width: 292,
    borderRadius: radii.lg,
    borderCurve: "continuous",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    backgroundColor: semantic.overlay.inverseStrong,
    padding: 14,
    gap: 14,
    ...shadows.soft,
  },
  productCardSelected: {
    borderColor: semantic.action.primary,
    backgroundColor: semantic.surface.subtle,
  },
  productCardPressed: {
    opacity: 0.9,
  },
  productVisual: {
    height: 144,
    borderRadius: radii.md,
    borderCurve: "continuous",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  productShine: {
    position: "absolute",
    top: 0,
    left: 42,
    width: 70,
    height: 180,
    backgroundColor: semantic.overlay.inverseSubtle,
    transform: [{ rotate: "18deg" }],
  },
  productBase: {
    position: "absolute",
    bottom: 32,
    width: 180,
    height: 38,
    borderRadius: 10,
    backgroundColor: semantic.overlay.darkSubtle,
  },
  productCopy: {
    gap: 8,
  },
  productHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  productName: {
    flex: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    lineHeight: 20,
  },
  productDescription: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
  },
  productPrice: {
    color: semantic.text.muted,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 17,
  },
  tagRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 7,
  },
  tag: {
    borderRadius: 999,
    backgroundColor: semantic.overlay.goldSubtle,
    paddingHorizontal: 9,
    paddingVertical: 5,
  },
  tagText: {
    color: semantic.accent.strong,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
  },
  folderPanel: {
    minWidth: 0,
    maxWidth: "100%",
    gap: 18,
  },
  folderHeader: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 13,
  },
  folderIcon: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: semantic.overlay.goldSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  folderCopy: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  folderTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 21,
  },
  folderBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
  },
  folderPreviewRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "center",
  },
  qrBox: {
    width: 122,
    height: 122,
    borderRadius: radii.md,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  qrBoxSmall: {
    alignSelf: "flex-start",
    marginTop: 8,
    width: 108,
    height: 108,
    borderRadius: radii.md,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  orderList: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 8,
  },
  orderItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  orderNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: semantic.overlay.forestSubtle,
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 24,
    textAlign: "center",
  },
  orderText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 18,
    flex: 1,
  },
  compactPanel: {
    gap: 14,
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
  },
  inlineHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  panelSmallTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    lineHeight: 20,
  },
  stack: {
    gap: 10,
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  checkText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 19,
    flex: 1,
  },
  noteText: {
    color: semantic.text.muted,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 18,
  },
  keepsakeGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  keepsakeCard: {
    flexBasis: 190,
    flexGrow: 1,
    minHeight: 138,
    borderRadius: radii.md,
    borderCurve: "continuous",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    backgroundColor: semantic.overlay.inverseStrong,
    padding: 14,
    gap: 9,
  },
  keepsakeCardSelected: {
    borderColor: semantic.action.primary,
    backgroundColor: semantic.surface.selected,
  },
  keepsakeIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: semantic.overlay.gold,
    alignItems: "center",
    justifyContent: "center",
  },
  keepsakeTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  keepsakeBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  caseCard: {
    borderRadius: radii.lg,
    borderCurve: "continuous",
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    backgroundColor: semantic.overlay.inverseStrong,
    padding: 18,
    gap: 14,
    ...shadows.soft,
  },
  caseTopline: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  caseTime: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
  },
  caseTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 30,
    lineHeight: 32,
  },
  caseMeta: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 19,
  },
  caseFacts: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  caseActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  directorSummary: {
    gap: 12,
    minWidth: 0,
  },
  directorSummaryText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 23,
    flexShrink: 1,
    minWidth: 0,
    width: "100%",
  },
  missingItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  severityDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: semantic.accent.soft,
  },
  requiredDot: {
    backgroundColor: semantic.action.destructive,
  },
  missingCopy: {
    gap: 2,
    flex: 1,
  },
  missingLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
  },
  missingSection: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
  },
  memorialPanel: {
    gap: 20,
  },
  memorialHero: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 18,
    alignItems: "center",
  },
  memorialPortrait: {
    width: 124,
    height: 124,
    borderRadius: 62,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: semantic.overlay.gold,
  },
  memorialCopy: {
    flex: 1,
    minWidth: 260,
    gap: 8,
  },
  memorialName: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 40,
    lineHeight: 42,
  },
  memorialBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  memorialColumns: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  memorialColumn: {
    flex: 1,
    minWidth: 280,
    gap: 12,
  },
  timelineItem: {
    borderLeftWidth: 2,
    borderLeftColor: semantic.overlay.forestStrong,
    paddingLeft: 12,
    gap: 3,
  },
  timelineTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  timelineMeta: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    lineHeight: 17,
  },
  fact: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
    flexBasis: 210,
    flexGrow: 1,
    padding: 12,
    borderRadius: radii.md,
    backgroundColor: semantic.surface.canvasAlt,
  },
  factCopy: {
    flex: 1,
    gap: 2,
  },
  factLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  factValue: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    lineHeight: 18,
  },
  nextActionBar: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    paddingTop: 4,
  },
});
